import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import DashboardShell from "../../components/layout/DashboardShell";
import { StatTile, Card } from "../../components/common/Primitives";
import DigestSnapshotCard from "../../components/admin/DigestSnapshotCard";
import api from "../../lib/api";
import { useProperty } from "../../lib/PropertyContext";
import { ArrowRight, AlertCircle, Mail, MousePointer, RefreshCw, ShieldAlert, CheckCircle2 } from "lucide-react";

export default function AdminDashboard() {
  const { activeProperty } = useProperty();
  const [stats, setStats] = useState({});
  const [nudge, setNudge] = useState(null);
  const [nudgeLoading, setNudgeLoading] = useState(false);
  const [deployHealth, setDeployHealth] = useState(null);

  const loadNudge = () => {
    setNudgeLoading(true);
    api.get("/admin/nudge-analytics?days=7")
      .then((r) => setNudge(r.data))
      .catch(() => setNudge({ campaigns: [], totals: { sent: 0, opened: 0, clicked: 0, open_rate: 0, click_rate: 0 } }))
      .finally(() => setNudgeLoading(false));
  };

  useEffect(() => {
    if (activeProperty?.slug === "venturaz_money") return;
    api.get("/admin/dashboard").then((r) => setStats(r.data || {}));
    api.get("/admin/deploy-health").then((r) => setDeployHealth(r.data)).catch(() => {});
    loadNudge();
  }, [activeProperty?.slug]);

  // Property-aware routing: when the operator switches to VenturaZ Money we
  // send them to the dedicated Money Dashboard instead of the equity
  // console. Debt-only roles land on /admin/debt directly (see rolePreset).
  if (activeProperty?.slug === "venturaz_money") {
    return <Navigate to="/admin/money/dashboard" replace />;
  }
  return (
    <DashboardShell title="VenturaZ Console" subtitle="ADMIN · OVERVIEW">
      <DeployHealthTile health={deployHealth} />
      <DigestSnapshotCard />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatTile label="Total Startups" value={stats.total_startups ?? 0} testId="admin-tile-startups" to="/admin/startups" />
        <StatTile label="Startups Approved" value={stats.startups_approved ?? 0} sub={`${stats.startups_pending ?? 0} pending`} testId="admin-tile-app-startups" to="/admin/approvals?tab=startups" />
        <StatTile label="Total Investors" value={stats.total_investors ?? 0} testId="admin-tile-investors" to="/admin/investors" />
        <StatTile label="Investors Approved" value={stats.investors_approved ?? 0} sub={`${stats.investors_pending ?? 0} pending`} testId="admin-tile-app-investors" to="/admin/approvals?tab=investors" />
        <StatTile label="Active Interests" value={stats.active_interests ?? 0} testId="admin-tile-interests" to="/admin/interests" />
        <StatTile label="Queries · Await Startup" value={stats.queries_pending_startup ?? 0} testId="admin-tile-q-startup" to="/admin/queries" />
        <StatTile label="Queries · Await Review" value={stats.queries_pending_review ?? 0} testId="admin-tile-q-review" to="/admin/queries" />
        <StatTile label="Active Deal Rooms" value={stats.active_deal_rooms ?? 0} testId="admin-tile-drooms" to="/admin/deal-rooms" />
        <StatTile label="KYC Pending" value={stats.kyc_pending ?? 0} testId="admin-tile-kyc" to="/admin/kyc-queue" />
        <StatTile label="Support Tickets" value={stats.support_tickets_open ?? 0} testId="admin-tile-support" to="/admin/notifications" />
        <StatTile label="Funding Demand" value={`₹${stats.funding_demand_cr ?? 0} Cr`} testId="admin-tile-demand" to="/admin/reports" />
      </div>

      <NudgeAnalyticsCard nudge={nudge} loading={nudgeLoading} onRefresh={loadNudge} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <QuickCard to="/admin/startups" title="Review Startup Applications" count={stats.startups_pending} />
        <QuickCard to="/admin/investors" title="Review Investor Approvals" count={stats.investors_pending} />
        <QuickCard to="/admin/queries" title="Moderate Query Responses" count={stats.queries_pending_review} />
      </div>
    </DashboardShell>
  );
}

function NudgeAnalyticsCard({ nudge, loading, onRefresh }) {
  const totals = nudge?.totals || {};
  return (
    <Card testId="nudge-analytics-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline mb-1">Nudge Analytics · 7 Days</div>
          <div className="text-xs text-ink-muted">Open + click stats for KYC / deck / onboarding campaigns.</div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="text-xs text-gold hover:text-gold/70 flex items-center gap-1 font-mono uppercase tracking-widest"
          data-testid="nudge-analytics-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> {loading ? "Loading" : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="border border-white/10 bg-white/60 backdrop-blur p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Sent</div>
          <div className="font-heading text-2xl text-ink mt-1" data-testid="nudge-totals-sent">{totals.sent ?? 0}</div>
        </div>
        <div className="border border-white/10 bg-white/60 backdrop-blur p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted flex items-center gap-1"><Mail size={11} /> Open Rate</div>
          <div className="font-heading text-2xl text-ink mt-1" data-testid="nudge-totals-open-rate">{totals.open_rate ?? 0}%</div>
          <div className="text-[10px] text-ink-muted mt-0.5">{totals.opened ?? 0} opens</div>
        </div>
        <div className="border border-white/10 bg-white/60 backdrop-blur p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted flex items-center gap-1"><MousePointer size={11} /> Click Rate</div>
          <div className="font-heading text-2xl text-ink mt-1" data-testid="nudge-totals-click-rate">{totals.click_rate ?? 0}%</div>
          <div className="text-[10px] text-ink-muted mt-0.5">{totals.clicked ?? 0} clicks</div>
        </div>
      </div>

      <table className="w-full text-sm" data-testid="nudge-analytics-table">
        <thead>
          <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-ink-muted border-b border-white/10">
            <th className="py-2">Campaign</th>
            <th className="py-2 text-right">Sent</th>
            <th className="py-2 text-right">Opens</th>
            <th className="py-2 text-right">Open %</th>
            <th className="py-2 text-right">Clicks</th>
            <th className="py-2 text-right">Click %</th>
          </tr>
        </thead>
        <tbody>
          {(nudge?.campaigns || []).map((c) => (
            <tr key={c.template} className="border-b border-white/5" data-testid={`nudge-row-${c.template}`}>
              <td className="py-2 text-ink">{c.label}</td>
              <td className="py-2 text-right text-ink font-mono">{c.sent}</td>
              <td className="py-2 text-right text-ink-muted font-mono">{c.opened}</td>
              <td className={`py-2 text-right font-mono ${c.open_rate >= 40 ? "text-gold" : "text-ink"}`}>{c.open_rate}%</td>
              <td className="py-2 text-right text-ink-muted font-mono">{c.clicked}</td>
              <td className={`py-2 text-right font-mono ${c.click_rate >= 10 ? "text-gold" : "text-ink"}`}>{c.click_rate}%</td>
            </tr>
          ))}
          {!nudge?.campaigns?.length && (
            <tr><td colSpan={6} className="py-4 text-center text-ink-muted text-xs">No nudge activity yet.</td></tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}

function QuickCard({ to, title, count }) {
  return (
    <Link to={to} className="border border-white/10 bg-white/70 backdrop-blur p-6 hover:border-gold/40 transition-colors block">
      <div className="flex items-center justify-between">
        <div>
          <AlertCircle className="text-gold" size={16} />
          <div className="font-heading text-ink text-lg mt-3">{title}</div>
          <div className="text-sm text-ink-muted mt-1">{count ?? 0} pending</div>
        </div>
        <ArrowRight className="text-gold" size={18} />
      </div>
    </Link>
  );
}


// ---------------------------------------------------------------------------
// DeployHealthTile — flags misconfigured env vars (FRONTEND_URL etc.) that
// would silently break outbound URLs like e-sign signer links or password
// reset emails. Only renders when the overall status is warn or error so the
// dashboard doesn't get cluttered when everything's fine.
// ---------------------------------------------------------------------------
function DeployHealthTile({ health }) {
  if (!health || health.overall === "ok" || !Array.isArray(health.checks)) return null;
  const bad = health.checks.filter((c) => c.status !== "ok");
  if (bad.length === 0) return null;
  const overall = health.overall;
  const tone =
    overall === "error"
      ? { border: "border-rose-500/60", bg: "bg-rose-500/[0.06]", icon: "text-rose-700", pill: "bg-rose-500/15 text-rose-800", label: "Action required" }
      : { border: "border-amber-500/60", bg: "bg-amber-500/[0.06]", icon: "text-amber-700", pill: "bg-amber-500/15 text-amber-800", label: "Heads up" };
  return (
    <div
      className={`mb-6 rounded-md border ${tone.border} ${tone.bg} p-4`}
      data-testid="deploy-health-tile"
    >
      <div className="flex items-start gap-3">
        <ShieldAlert className={`${tone.icon} mt-0.5 flex-shrink-0`} size={18} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading text-sm text-ink">Deployment sanity check</div>
            <span
              className={`text-[10px] font-heading uppercase tracking-widest px-2 py-0.5 rounded ${tone.pill}`}
              data-testid="deploy-health-overall"
            >
              {tone.label}
            </span>
            <span className="text-[10px] font-mono text-ink-muted">
              {bad.length} issue{bad.length === 1 ? "" : "s"} · {overall === "error" ? "outbound links will break" : "verify before next release"}
            </span>
          </div>
          <div className="mt-3 space-y-2">
            {bad.map((c) => (
              <div
                key={c.key}
                className="text-xs text-ink-soft flex items-start gap-2"
                data-testid={`deploy-health-check-${c.key}`}
              >
                <span className={`mt-0.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${c.status === "error" ? "bg-rose-600" : "bg-amber-500"}`} />
                <div className="min-w-0">
                  <div className="text-ink font-heading">
                    {c.label}
                    <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                      {c.resolved_from ? `via ${c.resolved_from}` : ""}
                    </span>
                  </div>
                  {c.value && (
                    <div className="font-mono text-[11px] text-ink-muted truncate">
                      current: {String(c.value)}
                    </div>
                  )}
                  {c.hint && <div className="text-[11px] mt-0.5">{c.hint}</div>}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-ink-muted flex items-center gap-1">
            <CheckCircle2 size={10} /> Fix in your deployment env vars, then redeploy — preview is unaffected.
          </div>
        </div>
      </div>
    </div>
  );
}
